import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendIntroVideo, updateVideoForm } from '../types/api';
import { useUser } from '../context/UserContext';
import { useLanguage } from "../context/LanguageContext";

import '../style/VideoForm.css';
const VideoForm: React.FC = () => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  //const { name, mobile, whatsapp, email, state, district, city, pinCode } = location.state || {};
  const { user, setUser } = useUser();

  const [loading, setLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [duration, setDuration] = useState('0:00');
  
  const { country, language } = useLanguage();
  
  // Configuration for showing the "watch later" option
  const showWatchLater = process.env.REACT_APP_WATCH_LATER_INTRO === 'true'; // This could be based on your app's configuration.

  const videoId = process.env.REACT_APP_INTRO_VIDEO_ID || 'F_69eDwnlbc';
  const testId = 'Gipdt4qC-Dk';
  const finalId = user?.name === 'ariseupp7Test' ? testId : videoId;

  useEffect(() => {
    const loadYouTubeAPI = () => {
      if (!(window as any).YT) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(tag);
      } else {
        createPlayer();
      }

      (window as any).onYouTubeIframeAPIReady = () => {
        createPlayer();
      };
    };

    const createPlayer = () => {
      if (!videoRef.current) return;

      playerRef.current = new (window as any).YT.Player('youtube-player', {
        videoId: finalId,
        playerVars: {
          enablejsapi: 1,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          iv_load_policy: 3,
        },
        events: {
          onReady: () => playerRef.current.setVolume(volume),
          onStateChange: async (event: any) => {
            if (event.data === window.YT.PlayerState.ENDED) {
              await updateVideoForm(user?.mobile ?? "", user?.whatsapp ?? "", "INTRO_VIDEO", "INTRO_VIDEO_COMPLETED");
              navigate('/decisionSurvey');
            }
          },
        },
      });
    };

    loadYouTubeAPI();

    const interval = setInterval(() => {
      if (playerRef.current?.getCurrentTime) {
        const current = playerRef.current.getCurrentTime();
        const total = playerRef.current.getDuration();
        setCurrentTime(formatTime(current));
        setDuration(formatTime(total));
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      playerRef.current?.destroy();
      delete (window as any).onYouTubeIframeAPIReady;
    };
  }, [finalId, navigate]);

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleWatchLater = async (e: React.MouseEvent) => {
    e.preventDefault();
       if (!user?.mobile) {
      console.error("User mobile not available");
      return;
    }
    //setUser({ name, mobile, whatsapp, language, email, country, state, district, city, pinCode });
    setUser({ ...user});
    setLoading(true);
    try {
      await sendIntroVideo(user?.name ?? "", user.mobile, user!.whatsapp ?? "", user!.email ?? "" );
      navigate('/videoCloseForm');
    } catch (error) {
      console.error("Failed to send video data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Play / Pause Function
  const handlePlayPause = () => {
    if (playerRef.current) {
      isPlaying ? playerRef.current.pauseVideo() : playerRef.current.playVideo();
      setIsPlaying(!isPlaying);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseInt(e.target.value, 10);
    setVolume(newVolume);
    if (playerRef.current) {
      playerRef.current.setVolume(newVolume);
      setIsMuted(newVolume === 0);
    }
  };

  const toggleMute = () => {
    if (playerRef.current) {
      if (isMuted) {
        playerRef.current.unMute();
        playerRef.current.setVolume(volume);
      } else {
        playerRef.current.mute();
      }
      setIsMuted(!isMuted);
    }
  };

  const getVolumeIcon = () => {
    if (isMuted || volume === 0) return '🔇';
    if (volume < 40) return '🔈';
    if (volume < 80) return '🔉';
    return '🔊';
  };

  return (
    <div className="div-container">
      <div className="video-wrapper" ref={videoRef} onClick={handlePlayPause}>
        <div id="youtube-player" />
        <div className="video-overlay" onContextMenu={(e) => e.preventDefault()} />
      </div>

      <div className="video-controls">
        <button className="video-play-button" onClick={handlePlayPause}>
          {isPlaying ? '❚❚' : '▶'}
        </button>

        <span className="video-volume-label" onClick={toggleMute} title={isMuted ? 'Unmute' : 'Mute'}>
          {getVolumeIcon()}
        </span>

        <input
          type="range"
          min="0"
          max="100"
          value={isMuted ? 0 : volume}
          onChange={handleVolumeChange}
          className="video-volume-slider"
        />

        <span className="video-timer">{currentTime} / {duration}</span>
      </div>

      {/* Conditionally render the "Watch Later" option */}
      {showWatchLater && (
        <p className="watch-later-text">
          If you want to watch the video later,
          <span className="watch-later-link" onClick={handleWatchLater}> watch later</span>.
        </p>
      )}
    </div>
  );
};

export default VideoForm;